import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of, switchMap } from 'rxjs';
import { Form } from '../models/form.model';
import { QuestionStructureOptions } from '../models/question/question-options.model';
import { QuestionType } from '../models/question/question-type.model';
import { Question } from '../models/question/question.model';
import { RendererQuestionsQuery } from '../queries/renderer-questions.query';

@Injectable({ providedIn: 'root' })
export class QuestionNumberService {
  private questionNumber: BehaviorSubject<string>[] = [];
  public questionNumber$: Observable<string>[] = [];

  private showQuestionNumber = new BehaviorSubject<boolean>(false);
  public showQuestionNumber$ = this.showQuestionNumber.asObservable();

  constructor(private rendererQuestionsQuery: RendererQuestionsQuery) {}

  public setQuestionNumber(questionId: string, questionNumber: string) {
    if (!this.questionNumber[questionId]) {
      this.questionNumber[questionId] = new BehaviorSubject(undefined);
      this.questionNumber$[questionId] =
        this.questionNumber[questionId].asObservable();
    }

    this.questionNumber[questionId].next(questionNumber);
  }

  public setShowQuestionNumber(showQuestionNumber: boolean) {
    this.showQuestionNumber.next(showQuestionNumber);
  }

  public setQuestionNumberForOrder(
    order: string[][],
    questions: Question[],
    level: number,
  ) {
    let questionNumber = 1;
    for (const questionRow of order) {
      for (const questionId of questionRow) {
        const question = questions.find((q) => q.id === questionId);
        if (question?.type === QuestionType.Information) {
          continue;
        }

        this.setQuestionNumber(
          questionId,
          this.getQuestionNumberForLevel(questionNumber, level),
        );
        questionNumber++;
      }
    }
  }

  public getShowQuestionNumber(questionType: QuestionType) {
    return (
      questionType !== QuestionType.Information && this.showQuestionNumber.value
    );
  }

  public getQuestionNumber(questionId: string) {
    return this.questionNumber[questionId]?.value;
  }

  public getQuestionNumberForLabel(questionId: string) {
    return this.showQuestionNumber$.pipe(
      switchMap((showQuestionNumber) =>
        showQuestionNumber && this.questionNumber$[questionId]
          ? this.questionNumber$[questionId].pipe(
              map((questionNumber) => `${questionNumber}. `),
            )
          : of(''),
      ),
    );
  }

  private getQuestionNumberForLevel(index: number, level: number): string {
    switch (level) {
      case 0: {
        return index.toString();
      }
      case 1: {
        return String.fromCodePoint(96 + index);
      }
      case 2: {
        return this.romanize(index);
      }
      default: {
        break;
      }
    }
  }

  private romanize(num: number): string {
    if (Number.isNaN(num)) {
      return undefined;
    }
    const digits = [...String(+num)];
    const key = [
      '',
      'c',
      'cc',
      'ccc',
      'cd',
      'd',
      'dc',
      'dcc',
      'dccc',
      'cm',
      '',
      'x',
      'xx',
      'xxx',
      'xl',
      'l',
      'lx',
      'lxx',
      'lxxx',
      'xc',
      '',
      'i',
      'ii',
      'iii',
      'iv',
      'v',
      'vi',
      'vii',
      'viii',
      'ix',
    ];
    let roman = '';
    let i = 3;
    while (i--) {
      roman = (key[+digits.pop() + i * 10] || '') + roman;
    }
    return Array.from({ length: +digits.join('') + 1 }).join('M') + roman;
  }

  public setQuestionNumbers(form: Form) {
    this.setShowQuestionNumber(form.structure.numberedQuestions);

    if (!form.structure.numberedQuestions) {
      return;
    }

    for (const section of form.sections) {
      this.setQuestionNumberForOrder(
        section.structure.order,
        section.questions,
        0,
      );

      const sectionQuestions =
        this.rendererQuestionsQuery.getQuestionsFromOrder(
          section.structure.order,
        );

      for (const question of sectionQuestions) {
        const structure = question.structure as QuestionStructureOptions;
        if (structure.followUpQuestionsOrder) {
          const followUpQuestions =
            this.rendererQuestionsQuery.getQuestionsFromOrder(
              structure.followUpQuestionsOrder,
            );
          this.setQuestionNumberForOrder(
            structure.followUpQuestionsOrder,
            followUpQuestions,
            1,
          );
          for (const followUpQuestion of followUpQuestions) {
            const followUpStructure =
              followUpQuestion.structure as QuestionStructureOptions;
            if (followUpStructure.followUpQuestionsOrder) {
              const followUpQuestionsLevel2 =
                this.rendererQuestionsQuery.getQuestionsFromOrder(
                  followUpStructure.followUpQuestionsOrder,
                );
              this.setQuestionNumberForOrder(
                followUpStructure.followUpQuestionsOrder,
                followUpQuestionsLevel2,
                2,
              );
            }
          }
        }
      }
    }
  }
}
